import React, { useState, useEffect } from 'react';
import { ecaService } from '../../lib/ecaService';

const ECAPage = ({ onTabChange }) => {
  const [selectedECA, setSelectedECA] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activeView, setActiveView] = useState('browse'); // browse, details, enroll, manage
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState('activities'); // activities, students, attendance, merit
  const [navigationSource, setNavigationSource] = useState(null);
  
  // State for dynamic data
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get current tenant ID
        const currentTenantId = ecaService.getCurrentTenantId();
        console.log('Current tenant ID:', currentTenantId);
        
        const [activitiesData, categoriesData, statsData] = await Promise.all([
          ecaService.getActivities(currentTenantId),
          ecaService.getCategories(),
          ecaService.getTenantStats(currentTenantId)
        ]);
        
        console.log('ECA Activities loaded:', activitiesData);
        console.log('ECA Categories loaded:', categoriesData);
        console.log('ECA Stats loaded:', statsData);
        
        setActivities(activitiesData || []);
        setCategories(categoriesData || []);
        
        if (!activitiesData || activitiesData.length === 0) {
          console.warn('No ECA activities found for tenant:', currentTenantId);
        }
        
      } catch (err) {
        console.error('Error loading ECA data:', err);
        setError(err.message || 'Failed to load ECA data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Check navigation source
  useEffect(() => {
    const source = localStorage.getItem('navigationSource');
    if (source) {
      try {
        const sourceInfo = JSON.parse(source);
        setNavigationSource(sourceInfo);
      } catch (e) {
        console.error('Error parsing navigation source:', e);
      }
    }
  }, []);

  const handleBackNavigation = () => {
    if (navigationSource?.source === 'class-dashboard' && navigationSource?.returnTo === 'teacher-directory') {
      // Go back to teacher directory (which shows class dashboard)
      // Use the onTabChange function to navigate to teacher-directory tab
      onTabChange('teacher-directory');
      // Clear the navigation source
      localStorage.removeItem('navigationSource');
    } else {
      // Default back behavior - go to main page
      window.history.back();
    }
  };

  const filteredECAs = activities.filter(eca => {
    const matchesSearch = eca.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (eca.name_en && eca.name_en.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (eca.description && eca.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === '全部' || 
                           (eca.eca_categories && eca.eca_categories.name === selectedCategory);
    return matchesSearch && matchesCategory;
  });

  const ECACard = ({ eca }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s'
    }}
    onClick={() => {
      setSelectedECA(eca);
      setActiveView('details');
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    }}>
      <div style={{
        width: '100%',
        height: '200px',
        backgroundImage: `url(${eca.image_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop'})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '8px',
        marginBottom: '12px'
      }} />
      
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px'}}>
        <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0}}>{eca.name}</h3>
        <div style={{
          padding: '4px 8px',
          borderRadius: '12px',
          backgroundColor: eca.status === 'active' ? '#dcfce7' : '#fee2e2',
          color: eca.status === 'active' ? '#065f46' : '#dc2626',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          {eca.status === 'active' ? '招生中' : '已截止'}
        </div>
      </div>

      <p style={{fontSize: '14px', color: '#6b7280', marginBottom: '12px', lineHeight: '1.5'}}>
        {eca.description || '暫無描述'}
      </p>

      <div style={{marginBottom: '12px'}}>
        <span style={{
          display: 'inline-block',
          padding: '4px 8px',
          backgroundColor: '#f3f4f6',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#374151',
          marginRight: '8px'
        }}>
          {eca.eca_categories ? eca.eca_categories.name : '未分類'}
        </span>
        <span style={{
          display: 'inline-block',
          padding: '4px 8px',
          backgroundColor: '#e0f2fe',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#0369a1'
        }}>
          {eca.age_group || '全年級'}
        </span>
      </div>

      <div style={{display: 'flex', gap: '16px', fontSize: '14px', color: '#4b5563', marginBottom: '12px'}}>
        <span>⏰ {eca.schedule}</span>
        <span>📍 {eca.venue}</span>
      </div>

      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <div>
          <span style={{fontSize: '14px', color: '#6b7280'}}>費用: </span>
          <span style={{fontSize: '16px', fontWeight: '600', color: eca.fee === 0 ? '#10b981' : '#1f2937'}}>
            {eca.fee === 0 ? '免費' : `$${eca.fee}`}
          </span>
        </div>
        <div style={{fontSize: '14px', color: '#6b7280'}}>
          {eca.current_enrollment}/{eca.max_capacity} 人
        </div>
      </div>
    </div>
  );

  const ECADetailPage = () => {
    // State for detail page data
    const [ecaActivities, setEcaActivities] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [meritRecords, setMeritRecords] = useState([]);
    const [detailLoading, setDetailLoading] = useState(true);
    const [detailError, setDetailError] = useState(null);

    // Load detail data when ECA is selected
    useEffect(() => {
      const loadDetailData = async () => {
        if (!selectedECA) return;
        
        try {
          setDetailLoading(true);
          const [membershipsData] = await Promise.all([
            ecaService.getActivityMemberships(selectedECA.id)
          ]);
          
          console.log('ECA Memberships loaded:', membershipsData);
          console.log('First membership sample:', membershipsData[0]);
          
          setEnrollments(membershipsData);
          
          // For now, we'll use empty arrays for activities, attendance, and merits
          // as these tables might not exist yet
          setEcaActivities([]);
          setAttendanceRecords([]);
          setMeritRecords([]);
        } catch (err) {
          setDetailError(err.message);
          console.error('Error loading ECA detail data:', err);
        } finally {
          setDetailLoading(false);
        }
      };
      
      loadDetailData();
    }, [selectedECA]);

    return (
      <div style={{backgroundColor: '#f5f5f5', minHeight: '100vh'}}>
        {/* Header */}
        <div style={{
          backgroundColor: '#8b5cf6',
          color: 'white',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button 
            onClick={() => {
              setSelectedECA(null);
              setActiveView('browse');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            ←
          </button>
          <div style={{flex: 1}}>
            <h1 style={{fontSize: '20px', fontWeight: '600', margin: 0}}>{selectedECA.name}</h1>
            <p style={{fontSize: '14px', opacity: 0.9, margin: '4px 0 0 0'}}>{selectedECA.name_en}</p>
          </div>
          <button
            onClick={() => setShowEnrollModal(true)}
            style={{
              backgroundColor: 'white',
              color: '#8b5cf6',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            立即報名
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '0'
        }}>
          {[
            { id: 'activities', label: '活動列表' },
            { id: 'students', label: '學員名單' },
            { id: 'attendance', label: '出席記錄' },
            { id: 'merit', label: '獎勵記錄' },
            { id: 'statistics', label: '統計分析' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: selectedTab === tab.id ? '#8b5cf6' : 'transparent',
                color: selectedTab === tab.id ? 'white' : '#6b7280',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                borderBottom: selectedTab === tab.id ? '2px solid #8b5cf6' : '2px solid transparent'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{padding: '16px'}}>
          {detailLoading ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <p style={{fontSize: '16px', color: '#6b7280', margin: 0}}>
                載入中...
              </p>
            </div>
          ) : detailError ? (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <p style={{fontSize: '16px', color: '#dc2626', margin: 0}}>
                載入失敗: {detailError}
              </p>
            </div>
          ) : (
            <>
              {selectedTab === 'activities' && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px'}}>
                    活動列表
                  </h3>
                  {ecaActivities.length > 0 ? (
                    <div style={{display: 'grid', gap: '12px'}}>
                      {ecaActivities.map(activity => (
                        <div key={activity.id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px'
                        }}>
                          <h4 style={{fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0'}}>
                            {activity.name}
                          </h4>
                          <p style={{fontSize: '14px', color: '#6b7280', margin: '0 0 8px 0'}}>
                            {activity.description}
                          </p>
                          <div style={{display: 'flex', gap: '16px', fontSize: '14px', color: '#4b5563'}}>
                            <span>⏰ {activity.schedule}</span>
                            <span>📍 {activity.venue}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{color: '#6b7280', textAlign: 'center', padding: '20px'}}>
                      暫無活動記錄
                    </p>
                  )}
                </div>
              )}
            </>
          )}

              {selectedTab === 'students' && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px'}}>
                    學員名單
                  </h3>
                  {enrollments.length > 0 ? (
                    <div style={{display: 'grid', gap: '12px'}}>
                      {enrollments.map(enrollment => (
                        <div key={enrollment.id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <h4 style={{fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px 0'}}>
                              {enrollment.saas_students?.name || 'Unknown Student'}
                            </h4>
                            <p style={{fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0'}}>
                              學號: {enrollment.saas_students?.student_code || 'N/A'}
                            </p>
                            <p style={{fontSize: '14px', color: '#6b7280', margin: '0 0 4px 0'}}>
                              班別: {enrollment.saas_students?.class_id || 'N/A'}
                            </p>
                            <p style={{fontSize: '14px', color: '#6b7280', margin: 0}}>
                              加入日期: {enrollment.join_date}
                            </p>
                          </div>
                          <div style={{textAlign: 'right'}}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: enrollment.status === 'enrolled' ? '#d1fae5' : '#fee2e2',
                              color: enrollment.status === 'enrolled' ? '#065f46' : '#dc2626'
                            }}>
                              {enrollment.status === 'enrolled' ? '在學' : '已退出'}
                            </span>
                            <p style={{fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0'}}>
                              {enrollment.payment_status === 'free' ? '免費' : 
                               enrollment.payment_status === 'paid' ? '已繳費' : 
                               enrollment.payment_status === 'pending' ? '待繳費' : enrollment.payment_status}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{color: '#6b7280', textAlign: 'center', padding: '20px'}}>
                      暫無學員記錄
                    </p>
                  )}
                </div>
              )}

              {selectedTab === 'attendance' && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px'}}>
                    出席記錄
                  </h3>
                  {attendanceRecords.length > 0 ? (
                    <div style={{display: 'grid', gap: '12px'}}>
                      {attendanceRecords.map(record => (
                        <div key={record.id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <h4 style={{fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px 0'}}>
                              {record.saas_students?.name || 'Unknown Student'}
                            </h4>
                            <p style={{fontSize: '14px', color: '#6b7280', margin: 0}}>
                              日期: {record.date}
                            </p>
                            {record.notes && (
                              <p style={{fontSize: '14px', color: '#4b5563', margin: '4px 0 0 0'}}>
                                備註: {record.notes}
                              </p>
                            )}
                          </div>
                          <div style={{textAlign: 'right'}}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              backgroundColor: record.status === 'present' ? '#d1fae5' : '#fee2e2',
                              color: record.status === 'present' ? '#065f46' : '#dc2626'
                            }}>
                              {record.status === 'present' ? '出席' : '缺席'}
                            </span>
                            {record.status === 'present' && record.arrival_time && record.departure_time && (
                              <p style={{fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0'}}>
                                {record.arrival_time} - {record.departure_time}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{color: '#6b7280', textAlign: 'center', padding: '20px'}}>
                      暫無出席記錄
                    </p>
                  )}
                </div>
              )}

              {selectedTab === 'merit' && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px'}}>
                    獎勵記錄
                  </h3>
                  <p style={{color: '#6b7280', textAlign: 'center', padding: '20px'}}>
                    暫無獎勵記錄
                  </p>
                </div>
              )}

              {selectedTab === 'statistics' && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px'}}>
                    統計分析
                  </h3>
                  
                  {/* Summary Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #0ea5e9',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{fontSize: '24px', fontWeight: '700', color: '#0ea5e9'}}>
                        {enrollments.length}
                      </div>
                      <div style={{fontSize: '14px', color: '#0369a1'}}>總學員數</div>
                    </div>
                    
                    <div style={{
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #10b981',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{fontSize: '24px', fontWeight: '700', color: '#10b981'}}>
                        {selectedECA?.max_capacity || 0}
                      </div>
                      <div style={{fontSize: '14px', color: '#059669'}}>活動容量</div>
                    </div>
                    
                    <div style={{
                      backgroundColor: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{fontSize: '24px', fontWeight: '700', color: '#f59e0b'}}>
                        {selectedECA?.current_enrollment || 0}
                      </div>
                      <div style={{fontSize: '14px', color: '#d97706'}}>當前人數</div>
                    </div>
                    
                    <div style={{
                      backgroundColor: '#fce7f3',
                      border: '1px solid #ec4899',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{fontSize: '24px', fontWeight: '700', color: '#ec4899'}}>
                        {selectedECA?.max_capacity && selectedECA?.current_enrollment ? 
                          Math.round((selectedECA.current_enrollment / selectedECA.max_capacity) * 100) : 0}%
                      </div>
                      <div style={{fontSize: '14px', color: '#db2777'}}>使用率</div>
                    </div>
                  </div>

                  {/* Student List with Performance */}
                  <div style={{marginBottom: '24px'}}>
                    <h4 style={{fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px'}}>
                      學員參與詳情
                    </h4>
                    <div style={{
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      {enrollments.length > 0 ? (
                        <div style={{display: 'grid', gap: '8px'}}>
                          {enrollments.map((enrollment, index) => (
                            <div key={enrollment.id} style={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              padding: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: '#8b5cf6',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '600'
                                }}>
                                  {index + 1}
                                </div>
                                <div>
                                  <div style={{fontSize: '14px', fontWeight: '600', color: '#1f2937'}}>
                                    {enrollment.saas_students?.name || 'Unknown Student'}
                                  </div>
                                  <div style={{fontSize: '12px', color: '#6b7280'}}>
                                    {enrollment.saas_students?.class_id || 'N/A'} • {enrollment.saas_students?.student_code || 'N/A'}
                                  </div>
                                </div>
                              </div>
                              <div style={{textAlign: 'right'}}>
                                <div style={{fontSize: '12px', color: '#6b7280'}}>
                                  加入: {enrollment.join_date}
                                </div>
                                <div style={{
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  backgroundColor: enrollment.status === 'enrolled' ? '#d1fae5' : '#fee2e2',
                                  color: enrollment.status === 'enrolled' ? '#065f46' : '#dc2626'
                                }}>
                                  {enrollment.status === 'enrolled' ? '在學' : '已退出'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p style={{color: '#6b7280', textAlign: 'center', padding: '20px'}}>
                          暫無學員記錄
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
        </div>
      </div>
    );
  };

  const EnrollmentModal = () => {
    if (!showEnrollModal || !selectedECA) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <h2 style={{fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '16px'}}>
            報名 {selectedECA.name}
          </h2>
          
          <div style={{marginBottom: '20px'}}>
            <h3 style={{fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '8px'}}>
              課程資訊
            </h3>
            <div style={{
              backgroundColor: '#f9fafb',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{margin: '0 0 8px 0', fontSize: '14px', color: '#374151'}}>
                <strong>上課時間:</strong> {selectedECA.schedule}
              </p>
              <p style={{margin: '0 0 8px 0', fontSize: '14px', color: '#374151'}}>
                <strong>上課地點:</strong> {selectedECA.venue}
              </p>
              <p style={{margin: '0 0 8px 0', fontSize: '14px', color: '#374151'}}>
                <strong>學費:</strong> {selectedECA.fee === 0 ? '免費' : `$${selectedECA.fee}`}
              </p>
              <p style={{margin: '0', fontSize: '14px', color: '#374151'}}>
                <strong>報名截止:</strong> {selectedECA.registration_deadline}
              </p>
            </div>

            <h3 style={{fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '8px'}}>
              入學要求
            </h3>
            <p style={{fontSize: '14px', color: '#6b7280', marginBottom: '16px'}}>
              {selectedECA.requirements || '暫無特殊要求'}
            </p>
          </div>

          <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
            <button
              onClick={() => setShowEnrollModal(false)}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                color: '#374151',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              取消
            </button>
            <button
              onClick={() => {
                alert('報名成功！我們會盡快與您聯繫。');
                setShowEnrollModal(false);
              }}
              style={{
                padding: '8px 16px',
                border: 'none',
                backgroundColor: '#8b5cf6',
                color: 'white',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              確認報名
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (activeView === 'details' && selectedECA) {
    return (
      <>
        <ECADetailPage />
        <EnrollmentModal />
      </>
    );
  }

  // Main Browse View
  return (
    <div style={{padding: '24px', maxWidth: '1200px', margin: '0 auto'}}>
      <div style={{textAlign: 'center', marginBottom: '32px'}}>
        {navigationSource?.source === 'class-dashboard' && (
          <div style={{textAlign: 'left', marginBottom: '16px'}}>
            <button onClick={handleBackNavigation} style={{
              backgroundColor: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}>
              ← 返回課室管理
            </button>
          </div>
        )}
        <h1 style={{fontSize: '24px', fontWeight: '600', margin: '0 0 8px 0'}}>課外活動</h1>
        <p style={{fontSize: '16px', color: '#6b7280', margin: 0}}>探索豐富多彩的課外活動，發展興趣愛好，提升綜合素質</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#6b7280',
          fontSize: '16px'
        }}>
          <div style={{marginBottom: '16px'}}>🔄 正在載入課外活動資料...</div>
          <div style={{fontSize: '14px', color: '#9ca3af'}}>請稍候</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          color: '#dc2626',
          textAlign: 'center'
        }}>
          <div style={{fontWeight: '600', marginBottom: '8px'}}>⚠️ 載入失敗</div>
          <div style={{fontSize: '14px'}}>{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              marginTop: '12px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            重新載入
          </button>
        </div>
      )}

      {/* Main Content - Only show when not loading and no errors */}
      {!loading && !error && (
        <>
          {/* Search and Filter Section */}
          <div style={{marginBottom: '24px'}}>
            <div style={{display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap'}}>
              <input
                type="text"
                placeholder="搜尋課外活動..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: '1',
                  minWidth: '200px',
                  padding: '12px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{
                  padding: '12px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white'
                }}
              >
                <option value="全部">全部類別</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary Statistics */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px'}}>
              課外活動概覽
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '16px'
            }}>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '24px', fontWeight: '700', color: '#0ea5e9'}}>
                  {activities.length}
                </div>
                <div style={{fontSize: '14px', color: '#6b7280'}}>總活動數</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '24px', fontWeight: '700', color: '#10b981'}}>
                  {activities.reduce((sum, activity) => sum + (activity.max_capacity || 0), 0)}
                </div>
                <div style={{fontSize: '14px', color: '#6b7280'}}>總容量</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '24px', fontWeight: '700', color: '#f59e0b'}}>
                  {activities.reduce((sum, activity) => sum + (activity.current_enrollment || 0), 0)}
                </div>
                <div style={{fontSize: '14px', color: '#6b7280'}}>總報名人數</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '24px', fontWeight: '700', color: '#8b5cf6'}}>
                  {categories.length}
                </div>
                <div style={{fontSize: '14px', color: '#6b7280'}}>活動類別</div>
              </div>
            </div>
          </div>

          {/* ECA Activities Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}>
            {filteredECAs.length > 0 ? (
              filteredECAs.map(eca => (
                <ECACard key={eca.id} eca={eca} />
              ))
            ) : (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '40px',
                color: '#6b7280',
                fontSize: '16px'
              }}>
                {searchTerm || selectedCategory !== '全部' ? '沒有找到符合條件的課外活動' : '暫無課外活動'}
              </div>
            )}
          </div>
        </>
      )}

      {/* Action Buttons Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '12px',
        margin: '20px 0 32px 0'
      }}>
        <button style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 12px',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.85rem',
          color: 'white',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          minHeight: '75px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          <i className="fas fa-chart-bar" style={{fontSize: '1.4rem', marginBottom: '6px'}}></i>
          考勤紀錄
        </button>
        
        <button style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 12px',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.85rem',
          color: 'white',
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          minHeight: '75px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          <i className="fas fa-money-bill-wave" style={{fontSize: '1.4rem', marginBottom: '6px'}}></i>
          繳費紀錄
        </button>
        
        <button style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 12px',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.85rem',
          color: 'white',
          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          minHeight: '75px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          <i className="fas fa-bullhorn" style={{fontSize: '1.4rem', marginBottom: '6px'}}></i>
          學校宣佈
        </button>
        
        <button style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 12px',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.85rem',
          color: 'white',
          background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
          minHeight: '75px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          <i className="fas fa-calendar-alt" style={{fontSize: '1.4rem', marginBottom: '6px'}}></i>
          學校活動
        </button>
        
        <button style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 12px',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.85rem',
          color: 'white',
          background: 'linear-gradient(135deg, #ec4899, #db2777)',
          minHeight: '75px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          <i className="fas fa-users" style={{fontSize: '1.4rem', marginBottom: '6px'}}></i>
          課外活動
        </button>
        
        <button style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 12px',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.85rem',
          color: 'white',
          background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
          minHeight: '75px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          <i className="fas fa-calendar" style={{fontSize: '1.4rem', marginBottom: '6px'}}></i>
          校曆表
        </button>
        
        <button style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '18px 12px',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.85rem',
          color: 'white',
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          minHeight: '75px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}>
          <i className="fas fa-chalkboard-teacher" style={{fontSize: '1.4rem', marginBottom: '6px'}}></i>
          教師通訊錄
        </button>
      </div>
    </div>
  );
};

export default ECAPage;
